<template>
  <div>

    <div class="ledger-expense-floating-button-container">
      <button v-shortkey.once="['ctrl', 'e']"
              v-if="state.ledger.selectedLedger.name &&
                state.account.me &&
                !state.ui.modalOpen &&
              state.app.online"
              class="btn btn-lg btn-success ledger-expense-floating-button"
              @click="showLedgerExpenseForm" @shortkey="showLedgerExpenseForm"
      >
        <i class="pe pe-plus-circle" aria-hidden="true"/>
      </button>
    </div>

    <b-modal
      id="ledgerExpenseForm"
      ref="ledgerExpenseForm"
      :key="'ledgerExpenseForm' + ledgerId" hide-footer
      title="Add expense"
      no-fade
      @hidden="hideLedgerExpenseForm"
      @shown="focusLedgerExpenseForm"
    >
      <div v-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-if="message" class="alert alert-success">{{ message }}</div>

      <div class="form-group row">
        <label for="expenseLabel" class="col-md-4 col-form-label text-md-right">Label</label>
        <div class="col-md-6">
          <input id="expenseLabel" ref="expenseLabel" v-model="form.expense.label" :class="{ 'is-invalid': errors.label }" type="text" class="form-control">
          <span v-if="errors.label" class="invalid-feedback">
            <div v-for="(error, index) in errors.label" :key="index"><strong>{{ error }}</strong></div>
          </span>
        </div>
      </div>

      <div class="form-group row">
        <label class="col-md-4 col-form-label text-md-right">Date</label>
        <div class="col-md-6">
          <input v-if="!form.expense.isNotDaySpecific" id="expenseFixDate" v-model="form.expense.fixDate" :class="{ 'is-invalid': errors.fixDate }" type="date" class="form-control">
          <span v-if="errors.fixDate" class="invalid-feedback">
            <div v-for="(error, index) in errors.fixDate" :key="index"><strong>{{ error }}</strong></div>
          </span>
          <input v-if="form.expense.isNotDaySpecific" id="expenseFuzzyDate" v-model="form.expense.fuzzyDate" :class="{ 'is-invalid': errors.fuzzyDate }" type="month" class="form-control">
          <span v-if="errors.fuzzyDate" class="invalid-feedback">
            <div v-for="(error, index) in errors.fuzzyDate" :key="index"><strong>{{ error }}</strong></div>
          </span>
        </div>
      </div>

      <div class="form-group row">
        <div class="col-12">
          <b-form-checkbox id="isNotDaySpecificCheckbox"
                           v-model="form.expense.isNotDaySpecific"
                           :value="true"
                           :unchecked-value="false">
            Not day specific
          </b-form-checkbox>
          <b-form-checkbox id="isRecurringCheckbox"
                           v-model="form.expense.isRecurring"
                           :value="true"
                           :unchecked-value="false">
            Is recurring
          </b-form-checkbox>
        </div>
      </div>

      <div v-if="form.expense.isRecurring">
        <div class="form-group row">
          <label for="recurringFrequencyInterval" class="col-md-4 col-form-label text-md-right">Frequency interval</label>
          <div class="col-md-3">
            <input id="recurringFrequencyInterval" v-model="form.expense.recurringFrequencyInterval" :class="{ 'is-invalid': errors.recurringFrequencyInterval }" type="number" min="1" class="form-control">
            <span v-if="errors.recurringFrequencyInterval" class="invalid-feedback">
              <div v-for="(error, index) in errors.recurringFrequencyInterval" :key="index"><strong>{{ error }}</strong></div>
            </span>
          </div>
        </div>

        <div class="form-group row">
          <label for="recurringFrequency" class="col-md-4 col-form-label text-md-right">Frequency</label>
          <div class="col-md-6">
            <b-form-select id="recurringFrequency" v-model="form.expense.recurringFrequency" :options="recurringFrequencyOptions" :class="{ 'is-invalid': errors.recurringFrequency }"/>
            <span v-if="errors.recurringFrequency" class="invalid-feedback">
              <div v-for="(error, index) in errors.recurringFrequency" :key="index"><strong>{{ error }}</strong></div>
            </span>
          </div>
        </div>

        <div class="form-group row">
          <label for="recurringEnds" class="col-md-4 col-form-label text-md-right">Recurrence end</label>
          <div class="col-md-6">
            <b-form-select id="recurringEnds" v-model="form.expense.recurringEnds" :options="form.recurringEndsOptions" :class="{ 'is-invalid': errors.recurringEnds }"/>
            <span v-if="errors.recurringEnds" class="invalid-feedback">
              <div v-for="(error, index) in errors.recurringEnds" :key="index"><strong>{{ error }}</strong></div>
            </span>
          </div>
        </div>

        <div v-if="form.expense.recurringEnds === 'occurrences'" class="form-group row">
          <label for="recurringOccurrences" class="col-md-4 col-form-label text-md-right"/>
          <div class="col-md-3">
            <input id="recurringOccurrences" v-model="form.expense.recurringOccurrences" :class="{ 'is-invalid': errors.recurringOccurrences }" type="number" min="1" class="form-control">
            <span v-if="errors.recurringOccurrences" class="invalid-feedback">
              <div v-for="(error, index) in errors.recurringOccurrences" :key="index"><strong>{{ error }}</strong></div>
            </span>
          </div>
        </div>

        <div v-if="form.expense.recurringEnds === 'date'" class="form-group row">
          <label for="recurringEndDate" class="col-md-4 col-form-label text-md-right"/>
          <div class="col-md-6">
            <input id="recurringEndDate" v-model="form.expense.recurringEndDate" :class="{ 'is-invalid': errors.recurringEndDate }" type="date" class="form-control">
            <span v-if="errors.recurringEndDate" class="invalid-feedback">
              <div v-for="(error, index) in errors.recurringEndDate" :key="index"><strong>{{ error }}</strong></div>
            </span>
          </div>
        </div>
        <div class="col-12 text-center text-yellow">
          {{ recurringText}}
        </div>
      </div>

      <hr>

      <div class="form-group row">
        <div class="col-12">
          <b-form-radio-group v-model="form.expense.selectedExpenseType"
                              :options="form.expenseTypeOptions"
          />
        </div>
      </div>

      <div v-if="form.expense.selectedExpenseType === 'estimate' || form.expense.selectedExpenseType === 'estimateAndPayment'" class="form-group row">
        <label for="expenseEstimate" class="col-md-4 col-form-label text-md-right">Estimate</label>
        <div class="col-md-6">
          <b-input-group :append="state.ledger.selectedLedger.currency | uppercase">
            <input id="expenseEstimate" v-model="form.expense.estimate" :class="{ 'is-invalid': errors.estimate }" type="number" pattern="[0-9]*" class="form-control">
          </b-input-group>
          <span v-if="errors.estimate" class="invalid-feedback d-block">
            <div v-for="(error, index) in errors.estimate" :key="index"><strong>{{ error }}</strong></div>
          </span>
        </div>
      </div>

      <div v-if="form.expense.selectedExpenseType === 'payment' || form.expense.selectedExpenseType === 'estimateAndPayment'" class="form-group row">
        <label for="expensePayment" class="col-md-4 col-form-label text-md-right">Payment</label>
        <div class="col-md-6">
          <b-input-group :append="state.ledger.selectedLedger.currency | uppercase">
            <input id="expensePayment" v-model="form.expense.payment" :class="{ 'is-invalid': errors.payment }" type="number" pattern="[0-9]*" class="form-control">
          </b-input-group>
          <span v-if="errors.payment" class="invalid-feedback d-block">
            <div v-for="(error, index) in errors.payment" :key="index"><strong>{{ error }}</strong></div>
          </span>
        </div>
      </div>

      <div class="form-group row mb-0">
        <div class="col-12">
          <button type="submit" class="btn btn-primary pull-right" @click.prevent="submitNewExpense()">
            <span v-if="this.$loading.isLoading('saving-ledger-expense')"><i class="pe pe-spinner fa-spin" aria-hidden="true"/></span> Add
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
let rrulestr = require('rrule').rrulestr
let moment = require('moment')

export default {
  data () {
    return {
      form: {
        expense: {
          label: '',
          fixDate: moment().format('YYYY-MM-DD'),
          fuzzyDate: moment().format('YYYY-MM'),
          isNotDaySpecific: false,
          isRecurring: false,
          recurringFrequencyInterval: 1,
          recurringFrequency: 'MONTHLY',
          recurringEnds: 'never',
          recurringOccurrences: 4,
          recurringEndDate: '',
          estimate: '',
          payment: '',
          selectedExpenseType: 'payment'
        },
        recurringEndsOptions: [
          { value: 'never', text: 'Never' },
          { value: 'occurrences', text: 'After X occurrences' },
          { value: 'date', text: 'On date' }
        ],
        expenseTypeOptions: [
          { value: 'payment', text: 'Payment' },
          { value: 'estimate', text: 'Estimate' },
          { value: 'estimateAndPayment', text: 'Estimate + Payment' }
        ]
      },
      message: '',
      error: '',
      errors: {}
    }
  },
  computed: {
    ...mapState({
      state: state => state
    }),
    ledgerId () {
      return this.$route.params.id
    },
    recurringFrequencyOptions () {
      if (this.form.expense.isNotDaySpecific) {
        return [
          { value: 'MONTHLY', text: 'Monthly' },
          { value: 'YEARLY', text: 'Yearly' }
        ]
      }

      return [
        { value: 'MONTHLY', text: 'Monthly' },
        { value: 'WEEKLY', text: 'Weekly' },
        { value: 'YEARLY', text: 'Yearly' }
      ]
    },
    recurringText () {
      let str = 'RRULE:'
      str = str + 'INTERVAL=' + this.form.expense.recurringFrequencyInterval + ';'
      str = str + 'FREQ=' + this.form.expense.recurringFrequency
      if (this.form.expense.recurringEnds === 'occurrences' && this.form.expense.recurringOccurrences) {
        str = str + ';COUNT=' + this.form.expense.recurringOccurrences
      }
      if (this.form.expense.recurringEnds === 'date' && this.form.expense.recurringEndDate) {
        let endDate = moment(new Date(this.form.expense.recurringEndDate))
        str = str + ';UNTIL=' + endDate.format('YYYYMMDD')
      }
      let rule = rrulestr(str)
      return rule.toText()
    }
  },
  created () {
    this.$eventHub.$on('selected-ledger-month-view-change', this.setFormDate)
  },
  beforeDestroy () {
    this.$eventHub.$off('selected-ledger-month-view-change')
  },
  methods: {
    ...mapMutations([
      'SET_MODAL_OPEN',
      'SET_MODAL_CLOSED'
    ]),
    ...mapActions([
      'createExpense'
    ]),
    showLedgerExpenseForm () {
      this.$refs.ledgerExpenseForm.show()
      this.SET_MODAL_OPEN()
    },
    hideLedgerExpenseForm () {
      this.$refs.ledgerExpenseForm.hide()
      this.SET_MODAL_CLOSED()
    },
    focusLedgerExpenseForm (e) {
      this.$refs.expenseLabel.focus()
    },
    setFormDate (date) {
      if (date > moment().format('YYYY-MM-DD')) {
        this.form.expense.fixDate = moment(date).format('YYYY-MM-DD')
        this.form.expense.fuzzyDate = moment(date).format('YYYY-MM')
      } else {
        this.form.expense.fixDate = moment().format('YYYY-MM-DD')
        this.form.expense.fuzzyDate = moment().format('YYYY-MM')
      }
    },
    submitNewExpense () {
      this.$loading.startLoading('saving-ledger-expense')
      this.errors = {}
      this.createExpense({ 'ledgerId': this.ledgerId, 'expense': this.form.expense })
        .then((data) => {
          this.$loading.endLoading('saving-ledger-expense')
          this.error = ''
          this.errors = {}
          this.form.expense.label = ''
          this.setFormDate(this.form.expense.fixDate)
          this.form.expense.isNotDaySpecific = false
          this.form.expense.isRecurring = false
          this.form.expense.estimate = ''
          this.form.expense.payment = ''
          this.form.expense.selectedExpenseType = 'payment'
          this.$refs.ledgerExpenseForm.hide()
          this.SET_MODAL_CLOSED()
        })
        .catch((data) => {
          this.error = data.message
          this.errors = data.errors || {}
          this.$loading.endLoading('saving-ledger-expense')
        })
    }
  }
}
</script>

<style>
  .ledger-expense-floating-button {
    z-index: 9999;
  }
</style>
